import { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  ChartBar, 
  Home, 
  Brain, 
  Stethoscope, 
  Beaker, 
  Target, 
  Search, 
  ClipboardList,
  ClipboardCopy,
  FileText 
} from 'lucide-react';
import { useGetData } from '../../../../hooks/useGetData';
import { EvaluadoInterface } from '../../../../interfaces/EvaluadoInterface';
import EvaluadoInfo from '../../../../components/EvaludoInfo';
import { ThemeContext } from '../../../../components/ThemeContext/ThemeContext';

export const ModulosListaScreen = () => {
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { reset } = useForm();
  const [isOpen, setIsOpen] = useState(false);
  const toggleBlind = () => setIsOpen(!isOpen);
  const { isDarkMode } = useContext(ThemeContext);

  const modules = [
    { title: 'Formatos Generales', icon: FileText, path: `/formatosG/${evaluadosList_id}` },
    { title: 'Investigación Socioeconómica', icon: ChartBar, path: `/socioeconomica/${evaluadosList_id}` },
    { title: 'Visita Domiciliaria', icon: Home, path: `/domiciliaria/${evaluadosList_id}` },
    { title: 'Evaluación Psicológica', icon: Brain, path: `/psicologica/${evaluadosList_id}` },
    { title: 'Evaluación Psicometrica', icon: ClipboardList, path: `/psicometrica/${evaluadosList_id}` },
    { title: 'Evaluación Médica', icon: Stethoscope, path: `/medica/${evaluadosList_id}` },
    { title: 'Evaluación Toxicológica', icon: Beaker, path: `/toxicologica/${evaluadosList_id}` },
    { title: 'Evaluación de Detección de Riesgos', icon: Target, path: `/poligrafo/${evaluadosList_id}` },
    { title: 'Reporte General del Área de Análisis de Información', icon: Search, path: `/informacion/${evaluadosList_id}` },
    { title: 'Otras Evaluaciones', icon: ClipboardList, path: `/otras_evaluacionesLinea/${evaluadosList_id}` },
    { title: 'Envío de Resultados', icon: ClipboardCopy, path: `/envio_resultadosLinea/${evaluadosList_id}` },
  ];

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
  };

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ['evaluado'],
  });

  useEffect(() => {  
    console.log(queryEvaluado.data);
    if (queryEvaluado.data) {
      reset({
        empresa: queryEvaluado.data.empresa?.nombre || '',
      });
    }
  }, [queryEvaluado.data, reset]);

  // Estilos adaptativos según el tema
  const getBackgroundStyle = () => {
    return isDarkMode 
      ? "bg-gradient-to-br from-gray-900 to-gray-800" 
      : "bg-gradient-to-br from-cyan-800 to-cyan-500";
  };

  const getButtonStyle = () => {
    return isDarkMode
      ? "bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 rounded-lg focus:outline-none border-2 border-gray-700"
      : "bg-gradient-to-r from-cyan-500 to-cyan-500 text-white py-3 rounded-lg focus:outline-none border-2 border-white";
  };

  const getCancelButtonStyle = () => {
    return isDarkMode ? "bg-red-600 text-white" : "bg-red-500 text-white";
  };

  const getCardStyle = () => {
    return isDarkMode 
      ? "bg-gray-800 rounded-lg shadow-lg overflow-hidden cursor-pointer h-full border border-gray-700" 
      : "bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer h-full";
  };

  const getIconStyle = () => {
    return isDarkMode ? "text-customCyan" : "text-indigo-500";
  };

  const getCardTitleStyle = () => {
    return isDarkMode ? "text-white" : "text-gray-800";
  };

  const getCardTextStyle = () => {
    return isDarkMode ? "text-gray-400" : "text-gray-600";
  };

  const getTitleStyle = () => {
    return isDarkMode ? "text-white" : "text-white";
  };

  return (
    <div className={`min-h-screen ${getBackgroundStyle()} p-8`}>
      {/* Botón "Cancelar" */}
      <div className="flex justify-between items-center mb-8">
        <Link to={`/tipoModulo/${evaluadosList_id}`} onClick={() => window.scrollTo(0, 0)}>
          <motion.button
            className={`${getCancelButtonStyle()} py-2 px-4 rounded-lg`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Regresar
          </motion.button>
        </Link>
      </div>

      {/* Título */}
      <h1 className={`text-4xl font-bold ${getTitleStyle()} mb-8 text-center flex-1`}>
        Modulos de evaluaciones
      </h1>

      {/* Botón de persiana */}
      <div className="flex justify-center">
        <motion.div className="mb-4 w-full max-w-[400px]">
          <motion.button
            onClick={toggleBlind}
            className={`w-[400px] text-lg font-semibold ${getButtonStyle()}`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isOpen ? 'Cerrar información del evaluado' : 'Información del evaluado'}
          </motion.button>
        </motion.div>
      </div>

      {/* Componente de información del evaluado con control externo */}
      <EvaluadoInfo 
        externalIsOpen={isOpen} 
        externalToggle={toggleBlind}
        hideButton={true} 
      />

      {/* Grid de módulos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        {modules.map((module, index) => (
          <Link to={module.path} key={module.title} className="no-underline" onClick={() => window.scrollTo(0, 0)}>
            <motion.div
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              custom={index}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={getCardStyle()}
            >
              <div className="p-6 flex flex-col justify-between h-full">
                <module.icon className={`w-12 h-12 ${getIconStyle()} mb-4`} />
                <h2 className={`text-xl font-semibold ${getCardTitleStyle()} mb-2`}>{module.title}</h2>
                <p className={getCardTextStyle()}>Click para ver detalles</p>
              </div>
            </motion.div>
          </Link>
        ))}
      </div>
    </div>
  );
}